import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, CheckCircle2, CreditCard, Wallet } from "lucide-react";
import { formatCHF, pos } from "../../services/posService";
import { ErrorBox, POSHeader, timeOf, useCurrentSession } from "./posShared";

export default function POSClosingPage() {
	const { loading, error, session, reload } = useCurrentSession();
	const [counted, setCounted] = useState("");
	const [notes, setNotes] = useState("");
	const [busy, setBusy] = useState(false);
	const [closeError, setCloseError] = useState("");
	const [closed, setClosed] = useState(null);

	const countedValue = Number(String(counted).replace(",", "."));
	const countedValid = counted.trim() !== "" && Number.isFinite(countedValue) && countedValue >= 0;
	const expected = Number(session?.expected_cash || 0);
	const difference = countedValid ? Math.round((countedValue - expected) * 100) / 100 : null;

	async function close() {
		if (!countedValid || !session) return;
		const message =
			difference === 0
				? "Clôturer la caisse ? La caisse est juste."
				: `Clôturer la caisse avec un écart de ${formatCHF(difference)} ?`;
		if (!window.confirm(message)) return;
		setBusy(true);
		setCloseError("");
		try {
			const { session: result } = await pos.closeSession(session.session_id, countedValue.toFixed(2), notes);
			setClosed(result);
		} catch (e) {
			setCloseError(e.message);
			reload();
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="min-h-screen bg-[#F7F3ED] text-[#34251D]">
			<POSHeader title="Clôture de caisse" icon={Wallet} />
			<main className="mx-auto max-w-4xl space-y-5 px-4 py-8 sm:px-6">
				<ErrorBox message={error} />
				{loading ? <p className="rounded-xl bg-white p-5 text-sm">Chargement…</p> : null}

				{closed ? <ClosedResult summary={closed} /> : null}

				{!loading && !session && !closed ? (
					<div className="rounded-2xl bg-white p-6">
						<p>Aucune caisse ouverte.</p>
						<Link to="/pos" className="mt-3 inline-block font-semibold underline">
							Retour au tableau de bord
						</Link>
					</div>
				) : null}

				{session && !closed ? (
					<>
						<section className="grid gap-4 md:grid-cols-2">
							<div className="rounded-2xl border border-[#E6E1D6] bg-white p-5">
								<h2 className="mb-3 flex items-center gap-2 text-lg font-bold">
									<Banknote size={20} /> Espèces
								</h2>
								<Line label="Fond de caisse" value={session.opening_cash} />
								<Line label={`Ventes en espèces (${session.cash.count})`} value={session.cash.total} sign="+" />
								<Line label="Entrées d'espèces" value={session.cash_in} sign="+" />
								<Line label="Sorties d'espèces" value={session.cash_out} sign="−" />
								<div className="mt-2 border-t pt-2">
									<Line label="Attendu dans le tiroir" value={session.expected_cash} strong />
								</div>
							</div>
							<div className="rounded-2xl border border-[#E6E1D6] bg-white p-5">
								<h2 className="mb-3 flex items-center gap-2 text-lg font-bold">
									<CreditCard size={20} /> Terminal
								</h2>
								<Line label={`Paiements par carte (${session.terminal.count})`} value={session.terminal.total} strong />
								<p className="mt-3 text-sm text-gray-500">
									Ces montants sont versés par Stripe sur ton compte bancaire. Ils ne sont pas dans le tiroir.
								</p>
								<div className="mt-3 border-t pt-2">
									<Line label="Total de la journée" value={session.sales_total} strong />
								</div>
							</div>
						</section>

						<section className="rounded-2xl border border-[#E6E1D6] bg-white p-6">
							<h2 className="text-xl font-bold">Comptage du tiroir</h2>
							<p className="mt-1 text-sm text-gray-500">Caisse ouverte à {timeOf(session.opened_at)}.</p>
							{session.awaiting_count ? (
								<div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
									{session.awaiting_count} paiement par terminal est encore en attente. Il sera vérifié auprès de Stripe
									au moment de la clôture.
								</div>
							) : null}
							<div className="mt-5 max-w-md space-y-4">
								<label className="block">
									<span className="mb-1 block font-medium">Espèces comptées (CHF)</span>
									<input
										value={counted}
										onChange={(e) => setCounted(e.target.value)}
										inputMode="decimal"
										placeholder="0.00"
										className="w-full rounded-xl border p-3 text-xl"
									/>
								</label>
								{difference !== null ? (
									<div
										className={`rounded-xl p-4 ${difference === 0 ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`}
									>
										<p className="text-sm">Écart</p>
										<p className="text-2xl font-bold">
											{difference > 0 ? "+" : ""}
											{formatCHF(difference)}
										</p>
										<p className="text-xs">
											{difference === 0
												? "La caisse est juste."
												: difference > 0
													? "Il y a plus d'espèces que prévu."
													: "Il manque des espèces."}
										</p>
									</div>
								) : null}
								<label className="block">
									<span className="mb-1 block font-medium">Notes (facultatif)</span>
									<textarea
										value={notes}
										onChange={(e) => setNotes(e.target.value)}
										rows={3}
										placeholder="Explication d'un écart, remarque…"
										className="w-full rounded-xl border p-3"
									/>
								</label>
								<ErrorBox message={closeError} />
								<button
									type="button"
									onClick={close}
									disabled={!countedValid || busy}
									className="w-full rounded-xl bg-[#34251D] p-4 font-semibold text-white disabled:opacity-50"
								>
									{busy ? "Clôture en cours…" : "Clôturer la caisse"}
								</button>
							</div>
						</section>
					</>
				) : null}
			</main>
		</div>
	);
}

function Line({ label, value, sign, strong }) {
	return (
		<div className={`flex justify-between py-1 ${strong ? "font-bold" : "text-gray-700"}`}>
			<span>{label}</span>
			<span>
				{sign && Number(value) ? `${sign} ` : ""}
				{formatCHF(value)}
			</span>
		</div>
	);
}

function ClosedResult({ summary }) {
	const diff = Number(summary.difference || 0);
	return (
		<section className="rounded-2xl border border-[#E6E1D6] bg-white p-6 text-center">
			<CheckCircle2 size={52} className="mx-auto text-emerald-600" />
			<h2 className="mt-3 text-2xl font-bold">Caisse clôturée</h2>
			<div className="mx-auto mt-5 max-w-sm text-left">
				<Line label="Terminal" value={summary.terminal.total} />
				<Line label="Espèces" value={summary.cash.total} />
				<Line label="Total de la journée" value={summary.sales_total} strong />
				<div className="mt-2 border-t pt-2">
					<Line label="Espèces attendues" value={summary.expected_cash} />
					<Line label="Espèces comptées" value={summary.counted_cash} />
					<Line label="Écart" value={summary.difference} strong />
				</div>
			</div>
			<p className={`mt-3 text-sm ${diff === 0 ? "text-emerald-700" : "text-amber-700"}`}>
				{diff === 0 ? "La caisse est juste." : "Un écart a été enregistré."}
			</p>
			<Link to="/pos" className="mt-5 inline-block rounded-xl bg-[#34251D] px-6 py-3 font-semibold text-white">
				Retour au tableau de bord
			</Link>
		</section>
	);
}
